import type {TicketChannel} from '../types/ticket';
import {isCustomerPortalAvailable} from '../../../../shared/util/customerPortalAvailability';

/**
 * How a ticket's arrival channel reads to an agent. Named once so the detail
 * header, the Details panel and any filter can never disagree, and so the raw
 * enum value (`customer_portal`) never reaches the page.
 *
 * This map is intentionally complete and unconditional: a ticket that was raised
 * through the portal carries `channel = 'customer_portal'` in the database
 * forever, and must always render a human-readable label regardless of whether
 * the current firm has the workspace enabled.
 */
export const CHANNEL_LABELS: Record<TicketChannel, string> = {
	manual: 'Manual',
	email: 'Email',
	phone: 'Phone',
	chat: 'Chat',
	internal: 'Internal',
	api: 'API',
	customer_portal: 'Client Workspace',
	direct: 'Direct',
};

/**
 * The design system glyph for a channel, where one fits. Email is the only channel
 * with an unambiguous glyph in the set, and it is also the one that matters — an
 * emailed ticket has a customer waiting on a reply. The rest read as text alone
 * rather than borrowing a glyph that means something else.
 */
export const CHANNEL_ICONS: Partial<Record<TicketChannel, string>> = {
	email: 'mail',
};

/**
 * The channels an agent may actively choose when editing a ticket. The Client
 * Workspace channel is only offered where the firm actually has the workspace
 * enabled — but the label stays defined for every account (see `CHANNEL_LABELS`)
 * so a portal-origin ticket never falls back to its raw enum value.
 *
 * This is a function rather than a constant so it reads portal availability at
 * render time, after login state is in place.
 */
export function selectableChannels(): TicketChannel[] {
	const all = Object.keys(CHANNEL_LABELS) as TicketChannel[];
	return isCustomerPortalAvailable() ? all : all.filter(c => c !== 'customer_portal');
}
